import { useEffect, useState } from 'react';
import { fetchMe, type MeResponse } from '@/apis/auth';

/** 兜底邮箱白名单（与后端 ``app/core/admin_acl.py`` 一致），仅在 RBAC 未铺开时使用。 */
export const ADMIN_EMAILS: readonly string[] = [
  'zhangzicheng@neolix.ai',
  'local@dev',
];

const ADMIN_EMAIL_SET = new Set(ADMIN_EMAILS.map((e) => e.toLowerCase()));

export const isAdminEmail = (email?: string | null): boolean => {
  if (!email) return false;
  return ADMIN_EMAIL_SET.has(email.trim().toLowerCase());
};

let cachedMePromise: Promise<MeResponse | null> | null = null;

const getMeCached = (): Promise<MeResponse | null> => {
  if (!cachedMePromise) {
    cachedMePromise = fetchMe().catch(() => null);
  }
  return cachedMePromise;
};

export const resetCurrentUserCache = () => {
  cachedMePromise = null;
};

export interface CurrentUserState {
  me: MeResponse | null;
  loading: boolean;
  isAdmin: boolean;
  hasPerm: (code: string) => boolean;
  hasRole: (code: string) => boolean;
}

/** 全局共享当前用户 hook：基于 ``/auth/me`` 拉取 user + roles + permissions。 */
export const useCurrentUser = (): CurrentUserState => {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getMeCached().then((m) => {
      if (!mounted) return;
      setMe(m);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  const roles = me?.roles ?? [];
  const permissions = me?.permissions ?? [];
  const isAdmin =
    roles.includes('admin') ||
    permissions.includes('menu:admin') ||
    isAdminEmail(me?.user?.email);

  const hasRole = (code: string) => roles.includes(code);
  // admin 全通；其他角色按显式权限码匹配
  const hasPerm = (code: string) => isAdmin || permissions.includes(code);

  return { me, loading, isAdmin, hasPerm, hasRole };
};
